// Team2.js - Contains functions for creating a modern team grid section
import { v4 as uuidv4 } from 'uuid';
import { DEVICES } from '../../constants/breakpoints';

/**
 * Creates a modern Team section with team members in a grid layout
 * @param {function} getHierarchyName - Function to get hierarchy name for elements
 * @param {string} currentPageId - The current page ID where the team section is being added
 * @param {function} setClassStylesRegistry - Function to update class styles registry
 * @param {function} setGlobalClassNames - Function to update global class names
 * @returns {Object} - The team section elements
 */
export const createTeamElements = (
  getHierarchyName, 
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Define class names
  const teamSectionClass = 'team2-section';
  const teamContainerClass = 'team2-container';
  const teamHeaderClass = 'team2-header';
  const teamTitleClass = 'team2-title';
  const teamSubtitleClass = 'team2-subtitle';
  const teamGridClass = 'team2-grid';
  const teamMemberClass = 'team2-member';
  const teamMemberInnerClass = 'team2-member-inner';
  const teamMemberImageClass = 'team2-member-image';
  const teamMemberContentClass = 'team2-member-content';
  const teamMemberInfoClass = 'team2-member-info';
  const teamMemberNameClass = 'team2-member-name';
  const teamMemberRoleClass = 'team2-member-role';
  const teamMemberSocialClass = 'team2-member-social';
  const teamMemberSocialIconClass = 'team2-social-icon';
  
  // Desktop Styles (default)
  const teamSectionStyles = {
    position: 'relative',
    padding: '80px 20px',
    backgroundColor: '#f8f9fa',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    overflowX: 'hidden'
  };

  const teamContainerStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    maxWidth: '1200px',
    width: '100%',
    margin: '0 auto',
    padding: '0 15px',
    gap: '40px'
  };

  const teamHeaderStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    maxWidth: '700px',
    width: '100%'
  };

  const teamTitleStyles = {
    fontSize: '40px',
    fontWeight: 'bold',
    color: '#1a202c',
    marginBottom: '16px',
    fontFamily: "'Poppins', sans-serif"
  };

  const teamSubtitleStyles = {
    fontSize: '18px',
    color: '#4a5568',
    lineHeight: '1.8',
    marginBottom: '24px'
  };

  const teamGridStyles = {
    display: 'grid',
    gridTemplateColumns: 'repeat(6, 1fr)',
    gap: '20px',
    width: '100%'
  };

  const teamMemberStyles = {
    borderRadius: '8px',
    overflow: 'hidden',
    position: 'relative',
    aspectRatio: '1/1',
    cursor: 'pointer',
    transition: 'transform 0.3s ease',
    width: '100%'
  };

  const teamMemberInnerStyles = {
    position: 'relative',
    width: '100%',
    height: '100%',
    transition: 'transform 0.5s ease',
    transformStyle: 'preserve-3d'
  };

  const teamMemberImageStyles = {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    backfaceVisibility: 'hidden'
  };

  const teamMemberContentStyles = {
    position: 'absolute',
    bottom: '0',
    left: '0',
    width: '100%',
    padding: '15px 10px',
    background: 'linear-gradient(0deg, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0) 100%)',
    color: '#ffffff',
    textAlign: 'center'
  };

  const teamMemberInfoStyles = {
    transition: 'transform 0.3s ease'
  };

  const teamMemberNameStyles = {
    fontSize: '16px',
    fontWeight: 'bold',
    marginBottom: '2px'
  };

  const teamMemberRoleStyles = {
    fontSize: '12px',
    color: '#e2e8f0',
    fontWeight: '500',
    marginBottom: '8px'
  };

  const teamMemberSocialStyles = {
    display: 'flex',
    justifyContent: 'center',
    gap: '8px',
    opacity: '0',
    transform: 'translateY(10px)',
    transition: 'opacity 0.3s ease, transform 0.3s ease'
  };

  const teamMemberSocialIconStyles = {
    width: '26px',
    height: '26px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    color: '#ffffff',
    transition: 'background-color 0.3s ease, transform 0.2s ease',
    cursor: 'pointer'
  };

  // Custom hover styles using CSS properties
  const teamMemberHoverStyles = {
    '&:hover': {
      transform: 'translateY(-5px)'
    },
    '&:hover .team2-member-social': {
      opacity: '1',
      transform: 'translateY(0)'
    },
    '&:hover .team2-social-icon:hover': {
      backgroundColor: '#3182ce',
      transform: 'scale(1.1)'
    }
  };

  // Tablet Styles
  const tabletTeamGridStyles = {
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '15px'
  };

  // Mobile Styles
  const mobileTeamGridStyles = {
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '10px'
  };

  // Update the classStylesRegistry
  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };
    
    // Desktop styles
    newRegistry[DEVICES.DESKTOP].set(teamSectionClass, teamSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(teamContainerClass, teamContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(teamHeaderClass, teamHeaderStyles);
    newRegistry[DEVICES.DESKTOP].set(teamTitleClass, teamTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(teamSubtitleClass, teamSubtitleStyles);
    newRegistry[DEVICES.DESKTOP].set(teamGridClass, teamGridStyles);
    newRegistry[DEVICES.DESKTOP].set(teamMemberClass, { ...teamMemberStyles, ...teamMemberHoverStyles });
    newRegistry[DEVICES.DESKTOP].set(teamMemberInnerClass, teamMemberInnerStyles);
    newRegistry[DEVICES.DESKTOP].set(teamMemberImageClass, teamMemberImageStyles);
    newRegistry[DEVICES.DESKTOP].set(teamMemberContentClass, teamMemberContentStyles);
    newRegistry[DEVICES.DESKTOP].set(teamMemberInfoClass, teamMemberInfoStyles);
    newRegistry[DEVICES.DESKTOP].set(teamMemberNameClass, teamMemberNameStyles);
    newRegistry[DEVICES.DESKTOP].set(teamMemberRoleClass, teamMemberRoleStyles);
    newRegistry[DEVICES.DESKTOP].set(teamMemberSocialClass, teamMemberSocialStyles);
    newRegistry[DEVICES.DESKTOP].set(teamMemberSocialIconClass, teamMemberSocialIconStyles);

    // Tablet styles
    newRegistry[DEVICES.TABLET].set(teamGridClass, tabletTeamGridStyles);

    // Mobile styles
    newRegistry[DEVICES.MOBILE].set(teamGridClass, mobileTeamGridStyles);
    newRegistry[DEVICES.MOBILE].set(teamTitleClass, { fontSize: '32px' });

    return newRegistry;
  });

  // Add classes to globalClassNames
  setGlobalClassNames(prev => new Set([
    ...prev,
    teamSectionClass,
    teamContainerClass,
    teamHeaderClass,
    teamTitleClass,
    teamSubtitleClass,
    teamGridClass,
    teamMemberClass,
    teamMemberInnerClass,
    teamMemberImageClass,
    teamMemberContentClass,
    teamMemberInfoClass,
    teamMemberNameClass,
    teamMemberRoleClass,
    teamMemberSocialClass,
    teamMemberSocialIconClass
  ]));
  
  // Generate base IDs
  const sectionId = Date.now();
  const containerId = sectionId + 1;
  const headerId = sectionId + 2;
  const titleId = sectionId + 3;
  const subtitleId = sectionId + 4;
  const gridId = sectionId + 5;
  
  // Team members data
  const teamMembers = [
    {
      name: 'Jessica Myers',
      role: 'CEO & Co-Founder',
      image: 'https://images.unsplash.com/photo-1508214751196-bcfd4ca60f91?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=600&q=80'
    },
    {
      name: 'Marcus Chen',
      role: 'CTO',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=600&q=80'
    },
    {
      name: 'Alicia Johnson',
      role: 'Lead Designer',
      image: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=600&q=80'
    },
    {
      name: 'David Park',
      role: 'Marketing Director',
      image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=600&q=80'
    },
    {
      name: 'Sophie Taylor',
      role: 'Product Manager',
      image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=600&q=80'
    },
    {
      name: 'James Wilson',
      role: 'Lead Developer',
      image: 'https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=600&q=80'
    },
    {
      name: 'Maya Rodriguez',
      role: 'UX Researcher',
      image: 'https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=600&q=80'
    },
    {
      name: 'Ryan Thompson',
      role: 'Business Analyst',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=600&q=80'
    }
  ];

  // Create base elements
  const elements = [
    // [0] Main Team Section
    {
      id: sectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'section',
      name: 'Team Section',
      parentId: null,
      className: teamSectionClass,
      styles: {},
      children: [containerId],
      text: '',
      attributes: {
        'data-element': 'team-section',
        'data-component': 'section',
        'data-section-type': 'team',
        'data-page-id': currentPageId
      }
    },
    
    // [1] Container
    {
      id: containerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Team Container',
      parentId: sectionId,
      hierarchyName: getHierarchyName('div'),
      className: teamContainerClass,
      styles: {},
      children: [headerId, gridId],
      text: '',
      attributes: {
        'data-element': 'team-container'
      }
    },
    
    // [2] Header Section
    {
      id: headerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Team Header',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: teamHeaderClass,
      styles: {},
      children: [titleId, subtitleId],
      text: '',
      attributes: {
        'data-element': 'team-header'
      }
    },
    
    // [3] Title 
    {
      id: titleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h2',
      name: 'Team Title',
      parentId: headerId,
      hierarchyName: getHierarchyName('h2'),
      className: teamTitleClass,
      styles: {},
      children: [],
      text: 'Meet Our Talented Team',
      attributes: {
        'data-element': 'team-title'
      }
    },
    
    // [4] Subtitle
    {
      id: subtitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Team Subtitle',
      parentId: headerId,
      hierarchyName: getHierarchyName('p'),
      className: teamSubtitleClass,
      styles: {},
      children: [],
      text: 'Our diverse team of experts brings creativity, innovation, and passion to every project we undertake.',
      attributes: {
        'data-element': 'team-subtitle'
      }
    },
    
    // [5] Team Grid
    {
      id: gridId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Team Grid',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: teamGridClass,
      styles: {},
      children: [],
      text: '',
      attributes: {
        'data-element': 'team-grid'
      }
    }
  ];

  // Dynamic ID offset for team members
  let idOffset = 6;
  
  // Ensure the grid starts with an empty children array
  elements[5].children = [];
  
  // Generate team member elements
  teamMembers.forEach((member, index) => {
    const memberId = sectionId + idOffset++;
    const memberInnerId = sectionId + idOffset++;
    const memberImageId = sectionId + idOffset++;
    const memberContentId = sectionId + idOffset++;
    const memberInfoId = sectionId + idOffset++;
    const memberNameId = sectionId + idOffset++;
    const memberRoleId = sectionId + idOffset++;
    const memberSocialId = sectionId + idOffset++;
    const twitterId = sectionId + idOffset++;
    const linkedinId = sectionId + idOffset++;
    const emailId = sectionId + idOffset++;
    
    // Add member to grid's children - COMMENTED OUT as we do this at the end
    // elements[5].children.push(memberId);
    
    // Member container
    elements.push({
      id: memberId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: `Team Member ${index + 1}`,
      parentId: gridId, // Explicitly using gridId here
      hierarchyName: getHierarchyName('div'),
      className: teamMemberClass,
      styles: {},
      children: [memberInnerId],
      text: '',
      attributes: {
        'data-element': 'team-member'
      }
    });
    
    // Member inner container
    elements.push({
      id: memberInnerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: `Team Member ${index + 1} Inner`,
      parentId: memberId,
      hierarchyName: getHierarchyName('div'),
      className: teamMemberInnerClass,
      styles: {},
      children: [memberImageId, memberContentId],
      text: '',
      attributes: {
        'data-element': 'team-member-inner'
      }
    });
    
    // Member image
    elements.push({
      id: memberImageId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: `Team Member ${index + 1} Image`,
      parentId: memberInnerId,
      hierarchyName: getHierarchyName('image'),
      className: teamMemberImageClass,
      styles: {},
      src: member.image,
      attributes: {
        'data-element': 'team-member-image',
        'alt': member.name
      }
    });
    
    // Member content overlay
    elements.push({
      id: memberContentId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: `Team Member ${index + 1} Content`,
      parentId: memberInnerId,
      hierarchyName: getHierarchyName('div'),
      className: teamMemberContentClass,
      styles: {},
      children: [memberInfoId],
      text: '',
      attributes: {
        'data-element': 'team-member-content'
      }
    });
    
    // Member info container
    elements.push({
      id: memberInfoId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: `Team Member ${index + 1} Info`,
      parentId: memberContentId,
      hierarchyName: getHierarchyName('div'),
      className: teamMemberInfoClass,
      styles: {},
      children: [memberNameId, memberRoleId, memberSocialId],
      text: '',
      attributes: {
        'data-element': 'team-member-info'
      }
    });
    
    // Member name
    elements.push({
      id: memberNameId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: `Team Member ${index + 1} Name`,
      parentId: memberInfoId,
      hierarchyName: getHierarchyName('h3'),
      className: teamMemberNameClass,
      styles: {},
      children: [],
      text: member.name,
      attributes: {
        'data-element': 'team-member-name'
      }
    });
    
    // Member role
    elements.push({
      id: memberRoleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: `Team Member ${index + 1} Role`,
      parentId: memberInfoId,
      hierarchyName: getHierarchyName('p'),
      className: teamMemberRoleClass,
      styles: {},
      children: [],
      text: member.role,
      attributes: {
        'data-element': 'team-member-role'
      }
    });
    
    // Member social links
    elements.push({
      id: memberSocialId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: `Team Member ${index + 1} Social`,
      parentId: memberInfoId,
      hierarchyName: getHierarchyName('div'),
      className: teamMemberSocialClass,
      styles: {},
      children: [twitterId, linkedinId, emailId],
      text: '',
      attributes: {
        'data-element': 'team-member-social'
      }
    });
    
    // Twitter icon
    elements.push({
      id: twitterId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Twitter Link',
      parentId: memberSocialId,
      hierarchyName: getHierarchyName('link'),
      className: teamMemberSocialIconClass,
      styles: {},
      children: [sectionId + idOffset++],
      text: '',
      attributes: {
        'data-element': 'social-link',
        'href': '#',
        'target': '_blank',
        'rel': 'noopener noreferrer',
        'aria-label': 'Twitter'
      }
    });
    
    // Twitter icon image
    elements.push({
      id: sectionId + (idOffset - 1),
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Twitter Icon',
      parentId: twitterId,
      hierarchyName: getHierarchyName('image'),
      styles: {
        width: '12px',
        height: '12px'
      },
      src: 'https://api.iconify.design/mdi:twitter.svg?color=%23ffffff',
      attributes: {
        'data-element': 'social-icon',
        'alt': 'Twitter'
      }
    });
    
    // LinkedIn icon
    elements.push({
      id: linkedinId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'LinkedIn Link',
      parentId: memberSocialId,
      hierarchyName: getHierarchyName('link'),
      className: teamMemberSocialIconClass,
      styles: {},
      children: [sectionId + idOffset++],
      text: '',
      attributes: {
        'data-element': 'social-link',
        'href': '#',
        'target': '_blank',
        'rel': 'noopener noreferrer',
        'aria-label': 'LinkedIn'
      }
    });
    
    // LinkedIn icon image
    elements.push({
      id: sectionId + (idOffset - 1),
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'LinkedIn Icon',
      parentId: linkedinId,
      hierarchyName: getHierarchyName('image'),
      styles: {
        width: '12px',
        height: '12px'
      },
      src: 'https://api.iconify.design/mdi:linkedin.svg?color=%23ffffff',
      attributes: {
        'data-element': 'social-icon',
        'alt': 'LinkedIn'
      }
    });
    
    // Email icon
    elements.push({
      id: emailId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Email Link',
      parentId: memberSocialId,
      hierarchyName: getHierarchyName('link'),
      className: teamMemberSocialIconClass,
      styles: {},
      children: [sectionId + idOffset++],
      text: '',
      attributes: {
        'data-element': 'social-link',
        'href': '#',
        'target': '_blank',
        'rel': 'noopener noreferrer',
        'aria-label': 'Email'
      }
    });
    
    // Email icon image
    elements.push({
      id: sectionId + (idOffset - 1),
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Email Icon',
      parentId: emailId,
      hierarchyName: getHierarchyName('image'),
      styles: {
        width: '12px',
        height: '12px'
      },
      src: 'https://api.iconify.design/mdi:email.svg?color=%23ffffff',
      attributes: {
        'data-element': 'social-icon',
        'alt': 'Email'
      }
    });
  });
  
  // After all elements are created and before returning
  
  // Reset the grid's children and fix all parent-child relationships
  const gridElement = elements.find(el => el.id === gridId);
  if (gridElement) {
    gridElement.children = [];
    
    // Find all team member elements and fix their parent
    for (let i = 0; i < elements.length; i++) {
      if (elements[i].name && elements[i].name.includes('Team Member') && 
          !elements[i].name.includes('Image') && !elements[i].name.includes('Name') && 
          !elements[i].name.includes('Role') && !elements[i].name.includes('Social') &&
          !elements[i].name.includes('Inner') && !elements[i].name.includes('Content') &&
          !elements[i].name.includes('Info')) {
        
        // This is a top-level team member container - set parent to grid
        elements[i].parentId = gridId;
        
        // Add to grid's children
        gridElement.children.push(elements[i].id);
      }
    }
  }
  
  return {
    teamSection: elements[0],
    elements: elements
  };
};

export default createTeamElements; 